import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import MobileNav from '../../components/ui/MobileNav';
import Icon from '../../components/AppIcon';
import { getLinkAnalytics } from '../../services/musicLinkService';
import { getClickSummary } from '../../services/clickTrackingService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const PLATFORM_COLORS = {
  spotify: '#1DB954',
  apple_music: '#fc3c44',
  youtube_music: '#FF0000',
  tiktok_sound: '#010101',
};

const PLATFORM_LABELS = {
  spotify: 'Spotify',
  apple_music: 'Apple Music',
  youtube_music: 'YouTube Music',
  tiktok_sound: 'TikTok Sound',
};

// Estimated streams multiplier per platform click
const STREAM_MULTIPLIER = {
  spotify: 3.2,
  apple_music: 2.8,
  youtube_music: 4.1,
  tiktok_sound: 5.5,
};

const StatCard = ({ icon, label, value, sub, color = 'primary' }) => {
  const colorMap = {
    primary: 'bg-primary/10 text-primary',
    green: 'bg-green-500/10 text-green-500',
    red: 'bg-red-500/10 text-red-500',
    purple: 'bg-purple-500/10 text-purple-500',
  };
  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${colorMap?.[color]}`}>
        <Icon name={icon} size={20} />
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-sm font-medium text-foreground mt-0.5">{label}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
};

const MusicLinkAnalytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [clickSummary, setClickSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) loadAnalytics();
  }, [user?.id]);

  const loadAnalytics = async () => {
    setLoading(true);

    // Load existing analytics (per-link breakdown)
    const { data } = await getLinkAnalytics(user?.id);
    setAnalytics(data);

    // Load real click summary from link_clicks table
    const slugs = (data?.links || [])?.map(l => l?.campaign_slug || l?.slug)?.filter(Boolean);
    const { data: summary } = await getClickSummary(slugs);
    setClickSummary(summary);

    setLoading(false);
  };

  const platformChartData = analytics
    ? Object.entries(analytics?.platformBreakdown || {})?.map(([key, count]) => ({
        name: PLATFORM_LABELS?.[key] || key,
        clicks: count,
        fill: PLATFORM_COLORS?.[key] || '#888',
      }))
    : [];

  const estimatedStreams = analytics
    ? Object.entries(analytics?.platformBreakdown || {})?.reduce((total, [platform, clicks]) => {
        return total + Math.round(clicks * (STREAM_MULTIPLIER?.[platform] || 2));
      }, 0)
    : 0;

  const linkPerformanceData = analytics?.links
    ?.filter(l => l?.totalClicks > 0)
    ?.sort((a, b) => b?.totalClicks - a?.totalClicks)
    ?.slice(0, 8)
    ?.map(l => ({ name: l?.title?.length > 20 ? l?.title?.substring(0, 20) + '…' : l?.title, clicks: l?.totalClicks })) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground text-sm">Loading analytics...</p>
          </div>
        </div>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 lg:pb-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1">Music Link Analytics</h1>
            <p className="text-muted-foreground text-sm">Track clicks, platform performance, and estimated streams</p>
          </div>
          <a
            href="/music-links"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Icon name="Link" size={16} />
            Manage Links
          </a>
        </div>

        {/* Real Click Tracking Summary */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-8">
          <h2 className="text-base font-semibold text-foreground mb-1">Click Tracking Summary</h2>
          <p className="text-xs text-muted-foreground mb-5">Live data from Supabase · public.link_clicks</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Total */}
            <div className="bg-muted/40 rounded-xl p-4 flex flex-col gap-1">
              <span className="text-2xl font-bold text-foreground">
                {clickSummary?.totalClicks?.toLocaleString() ?? '—'}
              </span>
              <span className="text-xs text-muted-foreground font-medium">Total Clicks</span>
            </div>
            {/* Spotify */}
            <div className="bg-[#1DB954]/10 rounded-xl p-4 flex flex-col gap-1">
              <span className="text-2xl font-bold text-[#1DB954]">
                {clickSummary?.platformBreakdown?.spotify?.toLocaleString() ?? '—'}
              </span>
              <span className="text-xs text-muted-foreground font-medium">Spotify</span>
            </div>
            {/* Apple Music */}
            <div className="bg-[#fc3c44]/10 rounded-xl p-4 flex flex-col gap-1">
              <span className="text-2xl font-bold text-[#fc3c44]">
                {clickSummary?.platformBreakdown?.apple_music?.toLocaleString() ?? '—'}
              </span>
              <span className="text-xs text-muted-foreground font-medium">Apple Music</span>
            </div>
            {/* YouTube Music */}
            <div className="bg-[#FF0000]/10 rounded-xl p-4 flex flex-col gap-1">
              <span className="text-2xl font-bold text-[#FF0000]">
                {clickSummary?.platformBreakdown?.youtube_music?.toLocaleString() ?? '—'}
              </span>
              <span className="text-xs text-muted-foreground font-medium">YouTube Music</span>
            </div>
            {/* TikTok Sound */}
            <div className="bg-muted/40 rounded-xl p-4 flex flex-col gap-1">
              <span className="text-2xl font-bold text-foreground">
                {clickSummary?.platformBreakdown?.tiktok_sound?.toLocaleString() ?? '—'}
              </span>
              <span className="text-xs text-muted-foreground font-medium">TikTok Sound</span>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon="MousePointerClick"
            label="Total Clicks"
            value={analytics?.totalClicks?.toLocaleString() || '0'}
            sub="All campaigns"
            color="primary"
          />
          <StatCard
            icon="Music"
            label="Est. Streams"
            value={estimatedStreams?.toLocaleString()}
            sub="Based on click rate"
            color="green"
          />
          <StatCard
            icon="Link"
            label="Active Links"
            value={analytics?.links?.length || '0'}
            sub="Campaign links"
            color="purple"
          />
          <StatCard
            icon="BarChart2"
            label="Top Platform"
            value={platformChartData?.[0]?.name || '—'}
            sub={platformChartData?.[0] ? `${platformChartData?.[0]?.clicks} clicks` : 'No data yet'}
            color="red"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Platform Breakdown */}
          <div className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-base font-semibold text-foreground mb-4">Platform Clicks</h2>
            {platformChartData?.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No click data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={platformChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="clicks"
                    label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
                    labelLine={false}
                  >
                    {platformChartData?.map((entry, index) => (
                      <Cell key={index} fill={entry?.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} clicks`, 'Clicks']} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Link Performance */}
          <div className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-base font-semibold text-foreground mb-4">Link Performance</h2>
            {linkPerformanceData?.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No click data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={linkPerformanceData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
                  <Tooltip
                    contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px' }}
                    labelStyle={{ color: 'var(--foreground)' }}
                  />
                  <Bar dataKey="clicks" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Platform Breakdown Table */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6">
          <h2 className="text-base font-semibold text-foreground mb-4">Platform Breakdown</h2>
          {platformChartData?.length === 0 ? (
            <p className="text-muted-foreground text-sm">No data yet. Share your links to start tracking.</p>
          ) : (
            <div className="space-y-3">
              {platformChartData?.map((platform) => {
                const pct = analytics?.totalClicks > 0 ? Math.round((platform?.clicks / analytics?.totalClicks) * 100) : 0;
                const platformKey = Object.keys(PLATFORM_LABELS)?.find(k => PLATFORM_LABELS?.[k] === platform?.name);
                const estStreams = platformKey ? Math.round(platform?.clicks * (STREAM_MULTIPLIER?.[platformKey] || 2)) : 0;
                return (
                  <div key={platform?.name} className="flex items-center gap-4">
                    <div className="w-32 text-sm font-medium text-foreground flex-shrink-0">{platform?.name}</div>
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: platform?.fill }}
                      />
                    </div>
                    <div className="text-sm text-foreground w-16 text-right flex-shrink-0">{platform?.clicks} clicks</div>
                    <div className="text-xs text-muted-foreground w-24 text-right flex-shrink-0">~{estStreams?.toLocaleString()} streams</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Influencer Performance */}
        {analytics?.influencerPerformance?.length > 0 && (
          <div className="bg-card border border-border rounded-2xl p-6 mb-6">
            <h2 className="text-base font-semibold text-foreground mb-4">Influencer Performance</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 text-muted-foreground font-medium">Influencer ID</th>
                    <th className="text-right py-2 text-muted-foreground font-medium">Total Clicks</th>
                    <th className="text-right py-2 text-muted-foreground font-medium">Top Platform</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics?.influencerPerformance?.map((inf) => {
                    const topPlatform = Object.entries(inf?.platforms || {})?.sort((a, b) => b?.[1] - a?.[1])?.[0];
                    return (
                      <tr key={inf?.influencerId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                        <td className="py-3 text-foreground font-mono text-xs">{inf?.influencerId?.substring(0, 12)}…</td>
                        <td className="py-3 text-right text-foreground font-semibold">{inf?.clicks}</td>
                        <td className="py-3 text-right text-muted-foreground">{topPlatform ? PLATFORM_LABELS?.[topPlatform?.[0]] || topPlatform?.[0] : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* All Links Table */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <h2 className="text-base font-semibold text-foreground mb-4">All Campaign Links</h2>
          {analytics?.links?.length === 0 ? (
            <p className="text-muted-foreground text-sm">No links created yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 text-muted-foreground font-medium">Title</th>
                    <th className="text-left py-2 text-muted-foreground font-medium hidden sm:table-cell">Slug</th>
                    <th className="text-right py-2 text-muted-foreground font-medium">Clicks</th>
                    <th className="text-right py-2 text-muted-foreground font-medium hidden md:table-cell">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics?.links?.map((link) => (
                    <tr key={link?.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-3 text-foreground font-medium">{link?.title}</td>
                      <td className="py-3 text-primary font-mono text-xs hidden sm:table-cell">/go/{link?.campaign_slug}</td>
                      <td className="py-3 text-right font-semibold text-foreground">{link?.totalClicks}</td>
                      <td className="py-3 text-right text-muted-foreground hidden md:table-cell">
                        {new Date(link.created_at)?.toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      <MobileNav />
    </div>
  );
};

export default MusicLinkAnalytics;
